import { type CandidateProfileData, type CandidateProfileEntryData, useCandidateProfileShow } from '@jobilla/nexus-sdk'
import { BriefcaseIcon, GraduationCapIcon, IdCardIcon, type LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

type ProfileSectionKey = keyof CandidateProfileData

export const sectionIcons: Record<ProfileSectionKey, LucideIcon> = {
	workHistory: BriefcaseIcon,
	education: GraduationCapIcon,
	licenses: IdCardIcon,
}

export const sectionColours: Record<ProfileSectionKey, { dot: string; icon: string }> = {
	workHistory: { dot: 'bg-sky-600', icon: 'text-sky-600' },
	education: { dot: 'bg-amber-500', icon: 'text-amber-600' },
	licenses: { dot: 'bg-emerald-600', icon: 'text-emerald-600' },
}

export const sectionKeys = Object.keys(sectionIcons) as ProfileSectionKey[]

export function CandidateProfileTab({ candidateId }: { candidateId: string }) {
	const profileQuery = useCandidateProfileShow(candidateId)

	return (
		<div className="h-full overflow-auto p-6">
			<div className="flex max-w-3xl flex-col gap-8">
				{sectionKeys.map(sectionKey => (
					<ProfileSection
						key={sectionKey}
						sectionKey={sectionKey}
						isPending={profileQuery.isPending}
						isError={profileQuery.isError}
						entries={profileQuery.data?.[sectionKey] ?? []}
					/>
				))}
			</div>
		</div>
	)
}

function ProfileSection(
	{ sectionKey, isPending, isError, entries }: {
		sectionKey: ProfileSectionKey
		isPending: boolean
		isError: boolean
		entries: CandidateProfileEntryData[]
	},
) {
	const { t } = useTranslation('common')

	return (
		<section className="flex flex-col gap-1.5">
			<h3 className="mb-4 text-xs font-semibold uppercase tracking-wider text-gray-500">
				{t(`candidateProfile.${sectionKey}.title`)}
			</h3>
			{isPending
				? <div aria-hidden={true} className="h-16 animate-pulse rounded-lg bg-gray-100" />
				: isError
				? <p role="alert" className="text-sm text-red-600">{t('candidateProfile.error')}</p>
				: entries.length === 0
				? <EmptyState sectionKey={sectionKey} />
				: <Timeline sectionKey={sectionKey} entries={entries} />}
		</section>
	)
}

function Timeline({ sectionKey, entries }: { sectionKey: ProfileSectionKey; entries: CandidateProfileEntryData[] }) {
	const { t, i18n } = useTranslation('common')
	const dateFormat = new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'short', year: 'numeric' })

	return (
		<ol className="flex flex-col">
			{entries.map((entry, entryIndex) => {
				const isLast = entryIndex === entries.length - 1
				const addedDate = dateFormat.format(new Date(entry.createdAt))
				const isEdited = new Date(entry.updatedAt).getTime() > new Date(entry.createdAt).getTime()

				return (
					<li key={entry.id} className="flex gap-3.5">
						<div aria-hidden={true} className="flex w-[13px] flex-col items-center">
							<span
								className={`mt-1.5 size-[11px] shrink-0 rounded-full ${sectionColours[sectionKey].dot}`}
							/>
							{isLast ? null : <span className="w-px flex-1 bg-gray-200" />}
						</div>
						<div className={`flex flex-1 flex-col gap-1 ${isLast ? '' : 'pb-5'}`}>
							<p className="ph-no-capture whitespace-pre-line text-sm leading-relaxed text-gray-700">
								{entry.contents}
							</p>
							<span className="text-xs text-gray-400">
								{isEdited
									? t(`candidateProfile.${sectionKey}.edited`, {
										editedDate: dateFormat.format(new Date(entry.updatedAt)),
										addedDate,
									})
									: t(`candidateProfile.${sectionKey}.added`, { date: addedDate })}
							</span>
						</div>
					</li>
				)
			})}
		</ol>
	)
}

function EmptyState({ sectionKey }: { sectionKey: ProfileSectionKey }) {
	const { t } = useTranslation('common')
	const Icon = sectionIcons[sectionKey]

	return (
		<div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-gray-300 px-6 py-8 text-center">
			<Icon className="size-7 text-gray-400" aria-hidden={true} />
			<p className="text-sm font-medium text-gray-700">{t(`candidateProfile.${sectionKey}.emptyTitle`)}</p>
		</div>
	)
}
